import {
  absenceTypeEnum,
  HOUR_SHEET_REVIEW_REASONS,
  hourSheets,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Flags the approver should manually verify, persisted on the sheet (text[]) so
// they survive submit. These mean the automatic km/allowance calc was unreliable
// and kilometers need a manual check. Long days are handled by the variance
// mechanism (varianceReasonCategory), not here.
export const hourSheetReviewReasonSchema = z.enum(HOUR_SHEET_REVIEW_REASONS)

export const selectHourSheetSchema = createSelectSchema(hourSheets, {
  reviewReasons: z.array(hourSheetReviewReasonSchema),
})

const hourSheetUserSchema = z
  .object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  })
  .nullable()

export const selectHourSheetWithUserSchema = selectHourSheetSchema.extend({
  user: hourSheetUserSchema,
  approvedByUser: hourSheetUserSchema.optional(),
})

// userId is auto-filled from auth context. Approval fields are gated by the
// router transform (only officers/admins may set them).
export const upsertHourSheetSchema = createInsertSchema(hourSheets, {
  reviewReasons: z.array(hourSheetReviewReasonSchema).optional(),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  userId: true,
})

export const hourSheetDraftVisitSchema = z.object({
  id: z.number(),
  startTime: z.string(),
  endTime: z.string(),
  title: z.string().nullable(),
  location: z.string().nullable(),
})

// A single day's visit as shown to the approver reviewing an hour sheet. Mirrors
// the inputs the allowance calc uses, so they can see why the auto allowance may
// be off: `hasLocation` is false when neither coordinates nor an address could be
// resolved, and `perDiemEligibleCity` is false for home-area cities (which never
// earn per diem regardless of distance).
export const hourSheetDayVisitSchema = z.object({
  id: z.number(),
  startTime: z.string(),
  endTime: z.string(),
  title: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  hasLocation: z.boolean(),
  perDiemEligibleCity: z.boolean(),
})

export const hourSheetDraftWarningSchema = z.enum([
  'NO_TEAM',
  'NO_OFFICE_CONFIGURED',
  'VISIT_MISSING_COORDS',
  'DISTANCE_LOOKUP_FAILED',
])

export const hourSheetDraftSchema = z.object({
  date: z.string(),
  teamId: z.number().nullable(),
  timesheetMode: z.enum(['visit_based', 'office_based']).nullable(),
  startedTime: z.string().nullable(),
  endedTime: z.string().nullable(),
  lunchMinutes: z.number(),
  driveFromOfficeMin: z.number().nullable(),
  driveToOfficeMin: z.number().nullable(),
  farthestVisitKm: z.number().nullable(),
  allowance: z.enum(['none', 'meal', 'half_per_diem', 'per_diem']),
  visits: z.array(hourSheetDraftVisitSchema),
  workDurationMin: z.number(),
  shouldPromptVarianceReason: z.boolean(),
  existingHourSheetId: z.number().nullable(),
  warnings: z.array(hourSheetDraftWarningSchema),
  reviewReasons: z.array(hourSheetReviewReasonSchema),
})

export const daySheetSubmittedSchema = z.object({
  date: z.string(),
  status: z.literal('submitted'),
  hourSheet: selectHourSheetSchema,
})

export const daySheetMissingSchema = z.object({
  date: z.string(),
  status: z.literal('missing'),
  visitCount: z.number(),
  firstVisitStart: z.string().nullable(),
  lastVisitEnd: z.string().nullable(),
  defaultStart: z.string().nullable(),
  defaultEnd: z.string().nullable(),
})

export const daySheetAbsenceSchema = z.object({
  date: z.string(),
  status: z.literal('absence'),
  absenceId: z.number(),
  absenceType: z.enum([
    'sick',
    'child_sick',
    'vacation',
    'paid_leave',
    'unpaid_leave',
    'parental_leave',
    'other',
  ]),
  absenceStatus: z.enum(['pending', 'approved', 'rejected']),
})

export const daySheetEntrySchema = z.discriminatedUnion('status', [
  daySheetSubmittedSchema,
  daySheetMissingSchema,
  daySheetAbsenceSchema,
])

// Payroll summary: one row per employee for a date range, aggregating everything
// the salary clerk needs (total hours, absences, tax-free allowance counts) so
// they don't have to gather it from the per-day approval list by hand.
export const hourSheetSummaryAllowanceSchema = z.object({
  count: z.number(),
  // The dates (YYYY-MM-DD) the allowance was earned, ascending — listed so the
  // clerk can cross-check individual days.
  dates: z.array(z.string()),
})

export const hourSheetSummaryAbsenceSchema = z.object({
  type: z.enum(absenceTypeEnum.enumValues),
  // Clipped to the queried range, so per-month dates and the day count reflect
  // only the days that fall inside the period being exported.
  startDate: z.string(),
  endDate: z.string(),
  days: z.number(),
})

export const hourSheetSummaryRowSchema = z.object({
  userId: z.number(),
  // The employee record id (for linking to /employees/:id). Null if the user has
  // no employee row — e.g. an officer who logged hours without being an employee.
  employeeId: z.number().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  totalMinutes: z.number(),
  workDays: z.number(),
  absences: z.array(hourSheetSummaryAbsenceSchema),
  absenceDays: z.number(),
  meal: hourSheetSummaryAllowanceSchema,
  halfPerDiem: hourSheetSummaryAllowanceSchema,
  perDiem: hourSheetSummaryAllowanceSchema,
})

export const hourSheetSummarySchema = z.array(hourSheetSummaryRowSchema)

export type HourSheet = z.infer<typeof selectHourSheetSchema>
export type HourSheetWithUser = z.infer<typeof selectHourSheetWithUserSchema>
export type UpsertHourSheet = z.infer<typeof upsertHourSheetSchema>
export type HourSheetDraft = z.infer<typeof hourSheetDraftSchema>
export type HourSheetDraftVisit = z.infer<typeof hourSheetDraftVisitSchema>
export type HourSheetDayVisit = z.infer<typeof hourSheetDayVisitSchema>
export type HourSheetDraftWarning = z.infer<typeof hourSheetDraftWarningSchema>
export type HourSheetReviewReason = z.infer<typeof hourSheetReviewReasonSchema>
export type DaySheetEntry = z.infer<typeof daySheetEntrySchema>
export type DaySheetSubmitted = z.infer<typeof daySheetSubmittedSchema>
export type DaySheetMissing = z.infer<typeof daySheetMissingSchema>
export type DaySheetAbsence = z.infer<typeof daySheetAbsenceSchema>
export type HourSheetSummaryRow = z.infer<typeof hourSheetSummaryRowSchema>
export type HourSheetSummaryAbsence = z.infer<
  typeof hourSheetSummaryAbsenceSchema
>
